import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const HILicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 24,
      cycleYears: 2,
      terminology: 'LU',
      highlights: [
        '24 Learning Units (LU) per 2-year cycle',
        'Health, Safety & Welfare (HSW) topics required',
        'AIA-CES and NCARB-approved providers accepted',
        'License renews biennially on December 31 (even years)',
      ],
      stateBoard: 'Hawaii DCCA — Board of Registration of Professional Engineers, Architects, Land Surveyors and Landscape Architects',
      boardUrl: 'https://cca.hawaii.gov/pvl/boards/engineer_architect/',
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: [
        '30 Professional Development Hours (PDH) per 2-year cycle',
        'Up to 15 PDH in one category may be applied',
        'Self-directed study and webinars accepted',
        'License renews biennially on December 31 (even years)',
      ],
      stateBoard: 'Hawaii DCCA — Board of Registration of Professional Engineers, Architects, Land Surveyors and Landscape Architects',
      boardUrl: 'https://cca.hawaii.gov/pvl/boards/engineer_architect/',
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 80,
      cycleYears: 2,
      terminology: 'CPE',
      highlights: [
        '80 CPE hours per 2-year period',
        '4 hours Ethics required each reporting period',
        'Minimum 20 hours per calendar year',
        'NASBA-approved sponsors preferred',
      ],
      stateBoard: 'Hawaii Board of Public Accountancy',
      boardUrl: 'https://cca.hawaii.gov/pvl/boards/accountancy/',
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 12,
      cycleYears: 1,
      terminology: 'CLE credits',
      highlights: [
        '12 CLE credits per year',
        '2 credits Ethics and Professional Responsibility',
        'Hawaii Supreme Court MCLE Rules apply',
        'Accredited sponsors and self-study permitted',
      ],
      stateBoard: 'Hawaii State Bar Association',
      boardUrl: 'https://www.hsba.org/HSBA/Attorneys/CLE/HSBA/Content/CLE.aspx',
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 30,
      cycleYears: 2,
      terminology: 'CE hours',
      highlights: [
        '30 CE hours per 2-year renewal period',
        'Competency-based activities accepted',
        'License renews biennially on December 31',
        'Hawaii Board of Nursing approves CE activities',
      ],
      stateBoard: 'Hawaii Board of Nursing',
      boardUrl: 'https://cca.hawaii.gov/pvl/boards/nursing/',
    },
    {
      id: 'PHARM',
      name: 'Pharmacist',
      icon: '💊',
      credits: 30,
      cycleYears: 2,
      terminology: 'CE hours',
      highlights: [
        '30 CE hours per 2-year cycle',
        'At least 15 hours must be live (non-home study)',
        '3 hours Law required',
        'License renews biennially on December 31 (even years)',
      ],
      stateBoard: 'Hawaii Board of Pharmacy',
      boardUrl: 'https://cca.hawaii.gov/pvl/boards/pharmacy/',
    },
  ];

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Hawaii Professional License Renewal Requirements',
      description:
        'Complete guide to Hawaii professional license renewal CE requirements for architects, engineers, CPAs, attorneys, nurses, and pharmacists.',
      url: 'https://cectrack.com/hi-license-renewal',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://cectrack.com' },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Hawaii License Renewal',
            item: 'https://cectrack.com/hi-license-renewal',
          },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How many CE hours do I need in Hawaii?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'CE requirements in Hawaii vary by profession. Architects need 24 LU per 2-year cycle, Professional Engineers need 30 PDH biennially, CPAs need 80 CPE hours every 2 years with 4 hours ethics, attorneys need 12 CLE credits per year with 2 hours ethics, RNs need 30 CE hours biennially, and pharmacists need 30 CE hours biennially.',
          },
        },
        {
          '@type': 'Question',
          name: 'Which agency handles professional licenses in Hawaii?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most Hawaii professional licenses are regulated by the Department of Commerce and Consumer Affairs (DCCA) Professional and Vocational Licensing (PVL) division. The Hawaii State Bar Association oversees attorney CLE requirements.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I use online courses for Hawaii CE requirements?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, most Hawaii professions accept online CE courses from accredited providers. Pharmacists have a restriction requiring at least 15 of their 30 CE hours to be live (non-home study) courses.',
          },
        },
      ],
    },
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Hawaii Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Hawaii professional license renewal CE requirements for architects, engineers, CPAs, attorneys, nurses, and more. Track all your HI CE credits in one place."
        keywords="Hawaii license renewal, HI CE requirements, Hawaii continuing education, Hawaii professional licenses, Hawaii architects, Hawaii engineers, Hawaii CPAs"
        canonical="https://cectrack.com/hi-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
            opacity: 0.4,
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1
                style={{
                  fontSize: '3rem',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                Hawaii Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Track your continuing education requirements across all your Hawaii professional
                licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to="/register"
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  Start Tracking Free
                </Link>
                <a
                  href="#professions"
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>6</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>50+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Board Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main id="main-content">
        {/* Professions Grid */}
        <section
          id="professions"
          aria-labelledby="professions-heading"
          style={{ padding: '80px 0' }}
        >
          <div className="container">
            <div className="text-center mb-5">
              <h2
                id="professions-heading"
                style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}
              >
                Hawaii CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Accurate continuing education data sourced from official Hawaii licensing boards
              </p>
            </div>
            <div className="row g-4">
              {professions.map((prof) => (
                <div key={prof.id} className="col-md-6 col-lg-4">
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div className="card-body p-4">
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                      <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                        {prof.name}
                      </h5>
                      <div
                        style={{
                          backgroundColor: '#e7f3ff',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          marginBottom: '1rem',
                        }}
                      >
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                          {prof.credits > 0
                            ? `${prof.credits} ${prof.terminology}`
                            : 'Mandated Training'}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          {prof.cycleYears === 1
                            ? 'Annual'
                            : prof.cycleYears === 2
                            ? 'Biennial'
                            : 'Triennial'}{' '}
                          Renewal
                        </div>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <div
                          style={{
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#6c757d',
                            marginBottom: '0.5rem',
                            textTransform: 'uppercase',
                          }}
                        >
                          Key Requirements:
                        </div>
                        <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                          {prof.highlights.map((h, i) => (
                            <li key={i} style={{ marginBottom: '0.3rem' }}>
                              {h}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div
                        style={{
                          paddingTop: '1rem',
                          borderTop: '1px solid #e9ecef',
                          fontSize: '0.85rem',
                          color: '#6c757d',
                        }}
                      >
                        <strong>{prof.stateBoard}</strong>
                        <br />
                        <a
                          href={prof.boardUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#3b82f6', textDecoration: 'none' }}
                        >
                          Visit State Board →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why CEC Track */}
        <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                  Simplify Your Hawaii License Management
                </h2>
                <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                  CEC Track automatically tracks requirements, calculates progress, and sends
                  reminders so you never miss a Hawaii renewal deadline.
                </p>
                <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                  {[
                    'Automated deadline tracking',
                    'Upload certificates to requirements',
                    'Real-time progress by category',
                    'Multi-state support',
                    'Secure cloud storage',
                    'Email notifications',
                  ].map((f, i) => (
                    <li key={i} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>
                        ✓
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-6">
                <div
                  className="p-5 text-center"
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  }}
                >
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                  <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                    Start Tracking Today
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                    Join professionals across Hawaii who trust CEC Track.
                  </p>
                  <Link
                    to="/register"
                    className="btn btn-primary btn-lg px-5 py-3"
                    style={{
                      fontWeight: '600',
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                  >
                    Create Free Account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* State Resources */}
        <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2
                id="resources-heading"
                style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}
              >
                Hawaii State Resources
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Official resources for Hawaii professional licensing
              </p>
            </div>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h4 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                      Hawaii DCCA — Professional &amp; Vocational Licensing
                    </h4>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                      Regulates architects, engineers, CPAs, nurses, and pharmacists through
                      the Department of Commerce and Consumer Affairs.
                    </p>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Phone:</strong> (808) 586-3000
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Website:</strong>{' '}
                      <a href="https://cca.hawaii.gov/pvl/" target="_blank" rel="noopener noreferrer">
                        cca.hawaii.gov/pvl
                      </a>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Address:</strong> 335 Merchant Street, Suite 301, Honolulu, HI 96813
                    </div>
                    <a
                      href="https://cca.hawaii.gov/pvl/boards/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary"
                    >
                      View All Boards
                    </a>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h4 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                      Hawaii State Bar Association — CLE
                    </h4>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                      Administers Mandatory Continuing Legal Education (MCLE) requirements for
                      Hawaii attorneys under Hawaii Supreme Court Rules.
                    </p>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Phone:</strong> (808) 537-1868
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Website:</strong>{' '}
                      <a href="https://www.hsba.org" target="_blank" rel="noopener noreferrer">
                        hsba.org
                      </a>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Address:</strong> 1100 Alakea Street, Suite 1000, Honolulu, HI 96813
                    </div>
                    <a
                      href="https://www.hsba.org/HSBA/Attorneys/CLE/HSBA/Content/CLE.aspx"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary"
                    >
                      CLE Requirements
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <OtherStates currentState="HI" />

        {/* CTA */}
        <section
          aria-label="Call to action"
          style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            color: 'white',
            padding: '80px 0',
          }}
        >
          <div className="container text-center">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              Ready to Simplify Your Hawaii License Renewal?
            </h2>
            <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
              Join professionals across Hawaii who never worry about missing CE requirements
            </p>
            <Link
              to="/register"
              className="btn btn-light btn-lg px-5 py-3"
              style={{ fontWeight: '600', borderRadius: '8px' }}
            >
              Get Started Free
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default HILicenseRenewal;
